/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subscription, throwError } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { filter, map, mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons/faCheckSquare';
import { OrganisationCollaboratorInterface } from '../../../models/interface/organisation/organisation-collaborator.interface';
import { OrganisationSharedService } from '../../../services/shared/organisation-shared/organisation-shared.service';
import { HelperService } from '../../../services/helper/helper.service';
import { OrganisationInterface } from '../../../models/interface/organisation/organisation.interface';
import { Store } from '@ngrx/store';
import { UserInterface } from '../../../models/interface/user.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { Actions, ofType } from '@ngrx/effects';
import { OrganisationActionTypes } from '../../../store/organisation/organisation.action';
import { HttpResponse } from '@angular/common/http';
import { OrganisationStore } from '../../../store/organisation/component-store/organisation.store';

type OrganisationDetailTab = 'studies' | 'collaborators' | 'newStudy';

@Component({
  selector: 'app-organisation-detail',
  templateUrl: './organisation-detail.component.html',
  styleUrls: ['./organisation-detail.component.scss'],
  providers: [OrganisationStore],
})
export class OrganisationDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('matTabOrganisation') matTabGroup: QueryList<MatTabGroup>;

  // Icons
  faCheckSquare = faCheckSquare;

  public isLoading$: Observable<boolean>;

  // Data provided by OrganisationService
  public organisation: OrganisationInterface;
  public collaborators: OrganisationCollaboratorInterface[];

  // Data provided by Profile Store
  public myUserId: number;

  public language: string;

  public isCollaborator$: Observable<boolean>;
  public isManager$: Observable<boolean>;
  public isOwner$: Observable<boolean>;

  public profile$: Observable<UserInterface>;

  private translationSubject = new BehaviorSubject<any>({});

  private organisation$: Observable<OrganisationInterface>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private isCollaboratorSubject = new BehaviorSubject<boolean>(false);
  private isManagerSubject = new BehaviorSubject<boolean>(false);
  private isOwnerSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private actRoute: ActivatedRoute,
    private router: Router,
    private sharedService: OrganisationSharedService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private actions$: Actions,
    private store: Store<{ myProfile: ProfileInterface }>,
    private organisationStore: OrganisationStore,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isCollaborator$ = this.isCollaboratorSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isOwner$ = this.isOwnerSubject.asObservable();
    this.profile$ = store.select('myProfile');
    this.organisation$ = this.organisationStore.organisation$;

    this.subscriptions.push(
      this.translateService
        .getTranslation(localStorage.getItem('language') !== null ? localStorage.getItem('language') : 'en')
        .subscribe((translations) => {
          this.translationSubject.next(translations);
        }),
    );
  }

  public get helper() {
    return this.helperService;
  }

  public get translation() {
    return this.translateService;
  }

  ngOnInit(): void {
    this.getOrganisationContent();

    // Subscription to language change
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.getOrganisationContent();
      })
    );

    // language
    if (localStorage.getItem('language') !== null) {
      this.language = localStorage.getItem('language');
    } else {
      this.language = 'en';
    }
    this.subscriptions.push(
      this.actRoute.params
        .pipe(
          map((v) => v.id),
          switchMap((organisationId) => {
            this.organisationStore.getOrganisation({ organisationId, include: 'roles' });
            return this.organisation$.pipe(skip(1), take(1));
          }),
        )
        .subscribe({
          next: (result) => {
            this.organisation = result;
            this.sharedService.pushOrganisation(this.organisation);
          },
          error: (error) => {
            throwError(() => error);
            this.router.navigateByUrl('/groups');
          },
          complete: () => {
            this.isLoadingSubject.next(false);
          },
        })
    );
  }

  ngAfterViewInit(): void {
    this.matTabGroup.changes.subscribe((_comps: QueryList<MatTabGroup>) => {
      this.helper.setMatTabActive(
        this.matTabGroup,
        this.router.url,
        this.translationSubject,
        this.translateService,
        'organisation-detail',
      );
    });
  }

  // Display and navigate to child component relative to this path
  public showChildComponent(child: OrganisationDetailTab): void {
    this.router.navigate([child], { relativeTo: this.actRoute });
  }

  // Listens to tab change
  public onChangeTab(event: MatTabChangeEvent): void {
    const tab = this.evaluateTab(event.tab.textLabel, this.translationSubject.value);
    if (tab !== '') {
      this.showChildComponent(tab);
    }
  }

  private evaluateTab(label: string, translate: any): OrganisationDetailTab | '' {
    if (translate) {
      if (translate['organisation-detail']['studies'] === label) {
        return 'studies';
      }
      if (translate['organisation-detail']['collaborators'] === label) {
        return 'collaborators';
      }
      if (translate['organisation-detail']['new_studies'] === label) {
        return 'newStudy';
      }
    }
    return '';
  }

  // Get and display detailed organisation details
  private getOrganisationContent(): void {
    this.isLoadingSubject.next(true);

    this.subscriptions.push(
      this.profile$
        .pipe(
          filter((user) => user !== null),
          mergeMap((value: ProfileInterface) => {
            // My current user ID
            this.myUserId = value.id;
            return this.actRoute.params;
          }),
          map((v) => v.id),
          mergeMap((organisationId) => {
            this.organisationStore.getOrganisation({ organisationId, include: 'roles' });
            const organisation = this.organisation$.pipe(skip(1), take(1));
            this.store.dispatch({
              type: OrganisationActionTypes.getOrganisationCollaboratorsType,
              payload: { organisationId },
            });
            const organisationCollaborators = this.actions$.pipe(
              ofType(
                OrganisationActionTypes.getOrganisationCollaboratorsSuccessType,
                OrganisationActionTypes.getOrganisationCollaboratorsErrorType,
              ),
              take(1),
            );
            return forkJoin({ organisation, organisationCollaborators });
          })
        )
        .subscribe({
          next: (results) => {
            this.organisation = results.organisation;
            this.sharedService.pushOrganisation(this.organisation);

            const organisationCollaborators: any = results.organisationCollaborators;
            if (organisationCollaborators.type === OrganisationActionTypes.getOrganisationCollaboratorsSuccessType) {
              const result: HttpResponse<any> = organisationCollaborators.response;
              this.collaborators = result.body.data;

              // Determine my highest role
              const myself = this.collaborators.find((user) => user.id === this.myUserId);
              if (myself !== undefined) {
                const myRoleSlug = this.helperService.getHighestRoleOfOrganisationCollaborator(myself);
                const isOrganisationOwner = myRoleSlug === 'organisation.owner';
                const isOrganisationManager = isOrganisationOwner || myRoleSlug === 'organisation.manager';
                if (isOrganisationManager) {
                  this.isCollaboratorSubject.next(true);
                  this.sharedService.pushIsCollaborator(true);
                  this.isManagerSubject.next(true);
                  if (isOrganisationOwner) {
                    this.isOwnerSubject.next(true);
                  }
                } else {
                  this.isCollaboratorSubject.next(false);
                  this.sharedService.pushIsCollaborator(false);
                }
              }
            }
            this.isLoadingSubject.next(false);
          },
          error: (error) => {
            throwError(() => error);
            this.router.navigateByUrl('/groups');
          },
          complete: () => {
            this.isLoadingSubject.next(false);
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
